<template>
  <div ref="rootRef" class="article-rich-content article-blocks-renderer">
    <div v-if="htmlContent" v-html="htmlContent"></div>

    <template v-else-if="normalizedBlocks.length">
      <template v-for="block in normalizedBlocks" :key="block.id">
        <h2 v-if="block.type === 'heading' && block.level !== 3" v-html="block.html || block.text"></h2>

        <h3 v-else-if="block.type === 'heading' && block.level === 3" v-html="block.html || block.text"></h3>

        <p
          v-else-if="block.type === 'paragraph'"
          class="article-block-paragraph"
          v-html="block.html || block.text"
        ></p>

        <ul v-else-if="block.type === 'list'">
          <li v-for="(item, index) in block.items" :key="`${block.id}-${index}`" v-html="item"></li>
        </ul>

        <figure
          v-else-if="block.type === 'image'"
          class="article-figure"
          :data-width="block.width || 100"
          :data-align="block.align || 'center'"
          :style="getMediaStyle(block)"
        >
          <img
            :src="block.src"
            :alt="block.alt || block.caption || '文章配图'"
            :style="getImageStyle(block)"
          >
          <figcaption v-if="block.caption" v-html="block.caption"></figcaption>
        </figure>

        <section
          v-else-if="block.type === 'video'"
          class="article-video"
          :data-width="block.width || 100"
          :data-align="block.align || 'center'"
          :style="getMediaStyle(block)"
        >
          <div class="article-video-frame">
            <iframe
              v-if="getYoutubeId(block.src)"
              class="article-video-embed"
              :src="`https://www.youtube.com/embed/${getYoutubeId(block.src)}`"
              title="文章视频"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            <video
              v-else
              class="article-video-player"
              controls
              preload="metadata"
              :src="block.src"
            ></video>
          </div>
          <p v-if="block.caption" class="article-video-note" v-html="block.caption"></p>
        </section>

        <section v-else-if="block.type === 'code'" class="article-code">
          <div class="article-code-head">
            <span>{{ block.filename || block.language || "code" }}</span>
            <button type="button" :data-copy-code="block.code">复制代码</button>
          </div>
          <pre><code>{{ block.code }}</code></pre>
        </section>

        <div
          v-else-if="block.type === 'callout'"
          class="article-callout"
          :class="`is-${block.tone || 'info'}`"
        >
          <strong v-if="block.title" v-html="block.title"></strong>
          <p v-if="block.text || block.html" v-html="block.html || block.text"></p>
        </div>
      </template>
    </template>

    <div v-else class="article-empty">这篇文章还没有正文内容。</div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  blocks: {
    type: Array,
    default: () => [],
  },
  html: {
    type: String,
    default: "",
  },
});

const rootRef = ref(null);

const normalizedBlocks = computed(() =>
  (Array.isArray(props.blocks) ? props.blocks : []).filter(
    (block) => block && block.type
  )
);

const htmlContent = computed(() => String(props.html || "").trim());

const getYoutubeId = (url = "") => {
  const match = String(url).match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/i
  );

  return match?.[1] || "";
};

const getMediaStyle = (block) => {
  const width = Number.parseInt(block?.width || 100, 10);
  if (!Number.isFinite(width) || width <= 0) {
    return null;
  }

  const safeWidth = Math.min(width, 100);
  const align = block?.align || "center";
  const style = {
    width: `${safeWidth}%`,
    maxWidth: "100%",
  };

  if (align === "left") {
    style.marginLeft = "0";
    style.marginRight = "auto";
  } else if (align === "right") {
    style.marginLeft = "auto";
    style.marginRight = "0";
  } else {
    style.marginLeft = "auto";
    style.marginRight = "auto";
  }

  return style;
};

const getImageStyle = () => ({
  width: "100%",
  maxWidth: "100%",
  display: "block",
  margin: "0 auto",
});

const normalizeRichMedia = () => {
  const root = rootRef.value;
  if (!(root instanceof HTMLElement)) {
    return;
  }

  const mediaNodes = root.querySelectorAll("figure.article-figure, section.article-video");
  mediaNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const align = node.getAttribute("data-align") || "center";
    node.setAttribute("data-align", align);
    node.style.maxWidth = node.style.maxWidth || "860px";

    if (align === "left") {
      node.style.marginLeft = "0";
      node.style.marginRight = "auto";
    } else if (align === "right") {
      node.style.marginLeft = "auto";
      node.style.marginRight = "0";
    } else {
      node.style.marginLeft = "auto";
      node.style.marginRight = "auto";
    }

    const image = node.querySelector("img");
    if (image instanceof HTMLImageElement) {
      image.style.display = "block";
      image.style.maxWidth = "100%";
      image.style.height = "auto";
      if (align === "left") {
        image.style.marginLeft = "0";
        image.style.marginRight = "auto";
      } else if (align === "right") {
        image.style.marginLeft = "auto";
        image.style.marginRight = "0";
      } else {
        image.style.marginLeft = "auto";
        image.style.marginRight = "auto";
      }
    }
  });
};

const fallbackCopyText = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  textarea.style.left = "-1000px";
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(textarea);
  return ok;
};

const getRenderedCodeText = (button) => {
  const codeSection = button.closest(".article-code");
  const renderedCode = codeSection?.querySelector("pre code")?.textContent;
  return String(renderedCode || button.getAttribute("data-copy-code") || "");
};

const handleCopyCode = async (event) => {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }

  const button = target.closest("[data-copy-code]");
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }

  const code = getRenderedCodeText(button);
  const originalText = button.textContent;

  let ok = false;
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(code);
      ok = true;
    } else {
      ok = fallbackCopyText(code);
    }
  } catch (error) {
    ok = fallbackCopyText(code);
  }

  button.textContent = ok ? "已复制" : "复制失败";

  window.setTimeout(() => {
    button.textContent = originalText;
  }, 1600);
};

onMounted(() => {
  rootRef.value?.addEventListener("click", handleCopyCode);
});

onBeforeUnmount(() => {
  rootRef.value?.removeEventListener("click", handleCopyCode);
});

watch(
  () => [htmlContent.value, normalizedBlocks.value.length],
  async () => {
    await nextTick();
    normalizeRichMedia();
  },
  { immediate: true }
);
</script>
